import { useState, useEffect } from 'react';
import "./FormResponses.css";
import { useDispatch, useSelector } from 'react-redux';
import { selectUIStates, selectUser, setUIStates } from '../../redux/features/appData';
import { FormHeader } from '../form-header/FormHeader';
import toast, { Toaster } from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router-dom';
import { DeleteFormResponsesPayload, DownloadFormResponsesSelectedPayload, formatDate3, FormExtended, FormResponseExtended, GetHiddenQuestionsForFormAndUserPayload, iconPath, Question, QuestionType, Response } from 'effno-core';
import { toastFail, toastSuccess } from '../../config';
import { apiDeleteFormResponses, apiGetFormResponsesForForm } from '../../services/ResponseService';
import { apiDownloadFormResponses, apiDownloadFormResponsesSelected, apiGetForm, apiGetFormFromPublicLink, apiGetFormNameFromPublishedId, apiGetHiddenQuestionsForFormAndUser } from '../../services/FormService';
import { isEmptyJSON } from '../../utils/utils';
import { ResponseDetails } from '../response-details/ResponseDetails';
import { ContactResponse } from '../config/responseSettings';
import { Contact } from '../shared/Contact';
import { FormResponsesActions } from './FormResponsesActions';
import { ICONS } from '../shared/media';
import FormResponsesMenu from './FormResponsesMenu';
import { FormResponsesTableSettings } from './FormResponsesTableSettings';
import { FormQuestionTag } from '../form-question/FormQuestionTag';
import { RatingStar } from '../shared/RatingStar';
import { FormResponsesDeleteModal } from './FormResponsesDeleteModal';
import { Download, Trash, X } from 'lucide-react';

const ColumnWidth ={
    [QuestionType.SHORT]: "250px",
    [QuestionType.LONG]: "250px",
    [QuestionType.EMAIL]: "200px",
    [QuestionType.WEBSITE]: "200px",
    [QuestionType.DATE]: "200px",
    [QuestionType.PHONE]: "200px",
    [QuestionType.MULTIPLE]: "300px",
    [QuestionType.FILE_UPLOAD]: "150px",
    [QuestionType.NUMBER]: "200px",
    [QuestionType.CONTACT]: "320px",
    [QuestionType.NPS]: "200px",
    [QuestionType.SCORE]: "200px",
    [QuestionType.RATING]: "200px",
    [QuestionType.END]: "200px",
    [QuestionType.SECTION]: "200px",

    // [QuestionType.CHECKBOX]: "150px",
    // [QuestionType.TIME]: "150px",
}

type FormResponsesProps = {
}

export const FormResponses = ({} : FormResponsesProps) => {
    
    const dispatch = useDispatch();
    const navigate = useNavigate();
    
    const params = useParams();

    const storeUser = useSelector(selectUser);
    const uiStates = useSelector(selectUIStates);

    const [originalFormName, setOriginalFormName] = useState<string | null>(null);
    const [form, setForm] = useState<FormExtended | null>(null);
    const [formResponses, setFormResponses] = useState<FormResponseExtended[] | null>(null);
    const [formId, setFormId] = useState<string | undefined>(undefined);
    const [formPublicLink, setFormPublicLink] = useState<string | undefined>(undefined);
    const [selectedRows, setSelectedRows] = useState<boolean[]>([]);

    // the grid
    // const [columns, setColumns] = useState<number | null>(null);
    const [columnNames, setColumnNames] = useState<string[] | null>(null);
    const [columnQuestions, setColumnQuestions] = useState<Question[] | null>(null);
    const [questionIds, setQuestionIds] = useState<string[] | null>(null);
    const [rowData, setRowData] = useState<JSX.Element[][] | null>(null);
    const [detailQuestionId, setDetailQuestionId] = useState<string | null>(null);

    const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);
    const [showTableSettings, setShowTableSettings] = useState<boolean>(false);
    const [hiddenQuestionIds, setHiddenQuestionIds] = useState<string[]>([]);
    const [formResponseHiddenQuestionId, setFormResponseHiddenQuestionId] = useState<string | null>(null);

    useEffect(() => {
        // if not logged in bounce there
        if (!storeUser) {
            navigate("/login");
            return;
        }
    }, []);
    
    useEffect(() => {
        // setFormId(params.formId);
        setFormPublicLink(params.formPublicLink);
    }, [params]);

    useEffect(() => {
        if (!form && formPublicLink) {
            (async () => {
                const theForm = await apiGetFormFromPublicLink(formPublicLink);

                if (!theForm) {
                    toast("Error getting form", toastFail);
                    return;
                }

                setForm(theForm);
                setFormId(theForm.id)

                // now ge the original form
                const theOriginalFormName = await apiGetFormNameFromPublishedId(theForm.id);

                if (!theOriginalFormName) {
                    toast("Error getting original form name", toastFail);
                    return;
                }

                setOriginalFormName(theOriginalFormName);
                
                const formResponses = await apiGetFormResponsesForForm(theForm.id);

                if (!formResponses) {
                    toast("Error getting responses", toastFail);
                    return;
                }

                // only show rows where there is at least one reponse to a valid question id
                // note that empty responses to valid questions (e.g. "did not anwer") are not filtered out
                const filteredResponses = formResponses.filter((response: FormResponseExtended) => {
                    let validResponses = 0;

                    response.responses.forEach((response: Response) => {
                        if (theForm.questions.find((question: Question) => question.id === response.questionId)) {
                            validResponses++;
                        }
                    });

                    return validResponses > 0;
                });

                setFormResponses(filteredResponses);

                // make the tables
                setColumnNames(getColumnNames(theForm));
                setColumnQuestions(getColumnQuestions(theForm));
                setQuestionIds(getQuestionIds(theForm));
                setRowData(getRowData(theForm, filteredResponses));
                setSelectedRows(new Array(filteredResponses.length).fill(false));

                // get the viewing info if it exists
                const payload : GetHiddenQuestionsForFormAndUserPayload = {
                    formId: theForm.id,
                    userId: storeUser?.id || "",
                }

                const userHiddenQuestions = await apiGetHiddenQuestionsForFormAndUser(payload);

                if (userHiddenQuestions) {
                    setHiddenQuestionIds(userHiddenQuestions);
                }
            })()
        }
    }, [formPublicLink])

    useEffect(() => {
        // if we have updated the hidden questions, we need to update the table
        if (form && formResponses) {
            setColumnNames(getColumnNames(form));
            setColumnQuestions(getColumnQuestions(form));
            setQuestionIds(getQuestionIds(form));
            setRowData(getRowData(form, formResponses));
            // setSelectedRows(new Array(formResponses.length).fill(false));
        }
    }, [hiddenQuestionIds]);

    // the first column is always going to be the submit date
    const getColumnNames = (theForm: FormExtended) : string[] => {
        const colNames : string[] = [];

        if (!theForm.questions) {
            return colNames;
        }

        const questionOrder = theForm.questionOrder ? JSON.parse(theForm.questionOrder) : null;

        if (!questionOrder || isEmptyJSON(questionOrder)) {
            return colNames;
        }

        colNames.push("Submit Date");

        questionOrder.forEach((questionId: string) => {
            if (!hiddenQuestionIds.some((hiddenQuestionId: string) => hiddenQuestionId === questionId)) {
                const questionIndex = theForm.questions.findIndex((question: Question) => question.id === questionId);
                if (questionIndex !== -1 && theForm.questions[questionIndex].type !== QuestionType.END && theForm.questions[questionIndex].type !== QuestionType.SECTION) {
                    colNames.push(theForm.questions[questionIndex].title + " : " + theForm.questions[questionIndex].id);
                }
            }
        });

        return colNames;
    }

    const getColumnQuestions = (theForm: FormExtended) : Question[] => {
        const colQuestions : Question[] = [];

        if (!theForm.questions) {
            return colQuestions;
        }

        const questionOrder = theForm.questionOrder ? JSON.parse(theForm.questionOrder) : null;

        if (!questionOrder || isEmptyJSON(questionOrder)) {
            return colQuestions;
        }

        colQuestions.push();

        questionOrder.forEach((questionId: string) => {
            if (!hiddenQuestionIds.some((hiddenQuestionId: string) => hiddenQuestionId === questionId)) {
                const questionIndex = theForm.questions.findIndex((question: Question) => question.id === questionId);
                if (questionIndex !== -1 && theForm.questions[questionIndex].type !== QuestionType.END && theForm.questions[questionIndex].type !== QuestionType.SECTION) {
                    colQuestions.push(theForm.questions[questionIndex]);
                }
            }
        });

        return colQuestions;
    }

    // the first column is always going to be the submit date so the question Id is empty
    const getQuestionIds = (theForm: FormExtended) : string[] => {
        const qIds : string[] = [];

        if (!theForm.questions) {
            return qIds;
        }

        const questionOrder = theForm.questionOrder ? JSON.parse(theForm.questionOrder) : null;

        if (!questionOrder || isEmptyJSON(questionOrder)) {
            return qIds;
        }

        qIds.push("");

        questionOrder.forEach((questionId: string) => {
            if (!hiddenQuestionIds.some((hiddenQuestionId: string) => hiddenQuestionId === questionId)) {
                const questionIndex = theForm.questions.findIndex((question: Question) => question.id === questionId);
                if (questionIndex !== -1 && theForm.questions[questionIndex].type !== QuestionType.END && theForm.questions[questionIndex].type !== QuestionType.SECTION) {
                    qIds.push(theForm.questions[questionIndex].id);
                }
            }
        });

        return qIds;
    }

    // this does not honor hidden questions as you want the whole list sometimes
    const getFormQuestionsInOrder = (theForm: FormExtended) : Question[] => {
        const questions : Question[] = [];

        if (!theForm.questions) {
            return [];
        }

        const questionOrder = theForm.questionOrder ? JSON.parse(theForm.questionOrder) : null;

        if (!questionOrder || isEmptyJSON(questionOrder)) {
            return [];
        }

        questionOrder.forEach((questionId: string) => {
            const questionIndex = theForm.questions.findIndex((question: Question) => question.id === questionId);
            if (questionIndex !== -1 && theForm.questions[questionIndex].type !== QuestionType.END && theForm.questions[questionIndex].type !== QuestionType.SECTION) {
                questions.push(theForm.questions[questionIndex]);
            }
        });

        return questions;
    }

    // the first column is always going to be the submit date
    const getRowData = (theForm: FormExtended, theFormResponses: FormResponseExtended[]) : JSX.Element[][] => {
        const rows : JSX.Element[][] = [];

        if (!theForm.questions) {
            return rows;
        }

        const questionOrder = theForm.questionOrder ? JSON.parse(theForm.questionOrder) : null;

        if (!questionOrder) {
            return rows;
        }

        theFormResponses.forEach((formResponse: FormResponseExtended) => {
            const row : JSX.Element[] = [];

            row.push(
                <span>
                    {formatDate3(new Date(formResponse.createdAt))}
                </span>
                );

            questionOrder.forEach((questionId: string) => {
                if (!hiddenQuestionIds.some((hiddenQuestionId: string) => hiddenQuestionId === questionId)) {
                    const questionIndex = theForm.questions.findIndex((question: Question) => question.id === questionId);
                    const responseIndex = formResponse.responses.findIndex((response: Response) => response.questionId === questionId);

                    if (theForm.questions[questionIndex].type !== QuestionType.END && theForm.questions[questionIndex].type !== QuestionType.SECTION) {
                        if (questionIndex !== -1 && responseIndex !== -1) {
                            row.push(
                                getResponseJSX(theForm.questions[questionIndex].type, formResponse.responses[responseIndex]));
                        } else {
                            row.push(
                                <span>
                                    empty
                                </span>
                            );
                        }
                    }
                }
            });

            rows.push(row);
        });

        return rows;
    }


    const getResponseJSX = (type: QuestionType, response: Response) : JSX.Element => {

        const values = JSON.parse(response.responseValues);

        if (!values || isEmptyJSON(values)) {
            return (
                <span>
                    [no values]
                </span>
            );
        }

        switch (type) {
            case QuestionType.SHORT:
            case QuestionType.LONG:
                return (
                    <span>
                        {values.text}
                    </span>
                );
            case QuestionType.EMAIL:
                return (
                    <span>
                        {values.email}
                    </span>
                );
            case QuestionType.WEBSITE:
                return (
                    <span>
                        {values.URL}
                    </span>
                );
            case QuestionType.DATE:
                return (
                    <span>
                        {values.date && values.date !== "" &&
                            formatDate3(new Date(values.date))
                        }
                        {/* {formatDate3(new Date(values.date))} */}
                    </span>
                );
            case QuestionType.PHONE:
                return (
                    <span>
                        {values.phone}
                    </span>
                );
            case QuestionType.MULTIPLE:
                let multiText = "";

                values.choices.forEach((value: any, index: number) => {
                    multiText += (index > 0) ? ", " : " ";
                    multiText += value;
                });

                return (
                    <span>
                        {multiText}
                    </span>
                );
            case QuestionType.FILE_UPLOAD:
                return (
                    <span>
                        {values.filename}
                    </span>
                );
            case QuestionType.NUMBER:
                return (
                    <span>
                        {values.number}
                    </span>
                );
            case QuestionType.CONTACT:
                return (
                    <Contact contact={values as ContactResponse} />
                );
            case QuestionType.NPS:
                return (
                    <span>
                        {values.score}
                    </span>
                );
            case QuestionType.SCORE:
                return (
                    <span>
                        {values.score}
                    </span>
                );
            case QuestionType.RATING:
                const design = JSON.parse(form?.formTheme?.settings || "{}");
                const color = design.answerColor || "red yellow";

                let starsJSX = [];

                for (let i=0; i<values.score; i++) {
                    starsJSX.push(
                            <RatingStar 
                                starNumber={i + 1}
                                starCount={values.score}
                                isMobileLayout={false}
                                color={color}
                            />
                        );
                }

                return (
                    <>
                        {starsJSX.map((star: JSX.Element) => {
                            return star;
                        })}
                    </>
                );
            case QuestionType.END:
                return (
                    <span>
                        [end screen]
                    </span>
                );
            case QuestionType.SECTION:
                return (
                    <span>
                        [section screen]
                    </span>
                );
            default:
                return (
                    <span>
                        [bad type]
                    </span>
                );
        }
    }

    const handleDownload = async () => {
        if (!formId) {
            return;
        }

        if (rowData && rowData.length === 0) {
            toast("No responses to download", toastSuccess);
            return;
        }

        const response = await apiDownloadFormResponses(formId);

        console.log("[FormResponses] handleDownload response: ", response);
    
        if (!response) {
            toast("Failed to download responses", toastFail);
            return;
        }
    
        const url = window.URL.createObjectURL(new Blob([response]));
        const a = document.createElement('a');
        a.href = url;
        a.download = 'responses.csv';
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
    }

    const handleToggleRow = (index: number) => {
        console.log("[FormResponses] handleToggleRow index: ", index);
        const newSelectedRows = [...selectedRows];
        newSelectedRows[index] = !newSelectedRows[index];
        setSelectedRows(newSelectedRows)
    }

    if (storeUser) {
        document.body.className = storeUser.theme;
    }

    const handleClearSelections = () => {
        setSelectedRows(new Array(selectedRows.length).fill(false));
    }

    const handleDownloadSelected = async () => {
        console.log("[FormResponses] handleDownloadSelected selectedRows: ", selectedRows);

        if (!formId || !formResponses || !selectedRows.some((selected: boolean) => selected)) {
            return;
        }

        const payload : DownloadFormResponsesSelectedPayload = {
            formId: formId,
            selectedFormResponseIds: formResponses.filter((response: FormResponseExtended, index: number) => {
                return selectedRows[index];
            }).map((response: FormResponseExtended) => response.id)
        }

        const response = await apiDownloadFormResponsesSelected(payload);

        console.log("[FormResponses] handleDownload selected response: ", response);
    
        if (!response) {
            toast("Failed to download responses", toastFail);
            return;
        }
    
        const url = window.URL.createObjectURL(new Blob([response]));
        const a = document.createElement('a');
        a.href = url;
        a.download = 'responses.csv';
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
    }

    const handleDeleteSelected = async () => {
        console.log("[FormResponses] handleDeleteSelected selectedRows: ", selectedRows);

        if (!form || !formResponses || !selectedRows.some((selected: boolean) => selected)) {
            return;
        }

        const selectedIds: string[] = [];
        selectedRows.forEach((selected: boolean, index: number) => {
            if (selected) {
                selectedIds.push(formResponses[index].id);
            }
        });

        const payload : DeleteFormResponsesPayload = {
            formResponseIds: selectedIds,
        }

        const deleted = await apiDeleteFormResponses(payload);

        if (!deleted) {
            toast("Error deleting responses", toastFail);
            return;
        }

        toast("Responses deleted", toastSuccess);

        // setFormResponses(filteredResponses);
        // setColumnNames(getColumnNames(theForm));
        // setQuestionIds(getQuestionIds(theForm));
        // setRowData(getRowData(theForm, filteredResponses));
        // setSelectedRows(new Array(filteredResponses.length).fill(false));

        const newResponses = formResponses.filter((response: FormResponseExtended, index: number) => {
            return !selectedRows[index];
        })

        setFormResponses(newResponses);
        setRowData(getRowData(form, newResponses));
        setSelectedRows(new Array(newResponses.length).fill(false));
        setShowDeleteConfirm(false);
        
        return;
    }

    const handleHideQuestion = async (questionId: string) => {
        setHiddenQuestionIds([...hiddenQuestionIds, questionId]);
        console.log("[FormResponses] handleHideQuestion questionId: ", questionId);
    }

    const questionOrder = form?.questionOrder ? JSON.parse(form.questionOrder) : null;

    // console.log("[FormResponses] form: ", form);
    // console.log("[FormResponses] formResponses: ", formResponses);
    console.log("[FormResponses] hiddenQuestions: ", hiddenQuestionIds);

    let gridLayout = "50px 150px";
    if (columnQuestions) {
        columnQuestions.forEach((question: Question) => {
            gridLayout += ` ${ColumnWidth[question.type]}`;
            // gridLayout += " auto";
        });
    }

    // console.log("[FormResponses] gridLayout: ", gridLayout);
    console.log("[FormResponses] detailQuestionId: ", detailQuestionId);



    return (
        <>
            <div id="form-responses">
                <div className="form-responses-header">
                    <FormHeader />
                </div>

                <div className="form-responses-body">
                    <div className="form-responses-container">

                        {rowData && rowData.length > 0 &&
                            <div 
                                className="form-responses-grid-container"
                                style={{gridTemplateColumns: gridLayout}}
                            >
                                {questionIds && columnQuestions && columnQuestions.map((question: Question, index: number) => {
                                    return (
                                        <>
                                            {index === 0 &&
                                                <>
                                                    <div className="form-responses-grid-item-sticky">
                                                        {}
                                                    </div>
                                                    <div className="form-responses-grid-item-sticky">
                                                        Submit Date
                                                    </div>
                                                </>
                                            }
                                            
                                            <div 
                                                className="form-responses-grid-item"                                                
                                                style={{cursor: "pointer"}}
                                                onClick={()=>
                                                    setDetailQuestionId(question.id)
                                                }
                                            >
                                                <div className="form-responses-grid-item-inner">
                                                    <div className="form-responses-grid-item-tag">
                                                        <FormQuestionTag 
                                                            key={"tag-" + question.id}
                                                            type={question.type} />
                                                    </div>
                                                    <div className="form-responses-grid-item-title">
                                                        {question.title}
                                                    </div>
                                                    <div className="form-responses-grid-item-menu">
                                                        <FormResponsesMenu 
                                                            key={"menu-" + question.id}
                                                            questionId={question.id}
                                                            onHide={handleHideQuestion}
                                                            onTableSettings={()=>setShowTableSettings(true)}
                                                        />
                                                    </div>
                                                    
                                                </div>
                                            </div>
                                            
                                        </>
                                        
                                    )})
                                }
                                {rowData && rowData.map((row: JSX.Element[], index: number) => {
                                    return (
                                        <>
                                        
                                            <div 
                                                className="form-responses-grid-data-sticky"
                                                style={{cursor: "pointer"}}
                                            >
                                                <input
                                                    key={"select" + index}
                                                    type="checkbox"
                                                    name={"select-" + index}
                                                    checked={selectedRows[index]}
                                                    onChange={()=>handleToggleRow(index)}
                                                />
                                            </div>
                                            
                                            {row && row.map((data: JSX.Element, index) => {
                                                return (
                                                    <>
                                                        <div className={index===0 ?
                                                            "form-responses-grid-data-sticky-second" :
                                                            "form-responses-grid-data"
                                                            }
                                                        >
                                                            {data}
                                                        </div> 
                                                    </>
                                                )
                                            })}
                                        </>
                                    )
                                    })
                                }
                                {/* {rowData && rowData.length === 0 &&
                                    <div className="form-responses-grid-data-zero">
                                        No responses yet
                                    </div>
                                } */}
                            </div>
                        }

                        {rowData && rowData.length === 0 &&
                            <div className="form-responses-data-zero">
                                No responses yet
                            </div>
                        }
                    </div>

                    

                    <FormResponsesActions 
                        onDownload={handleDownload}
                        onOpenTableSettings={()=>setShowTableSettings(true)}
                    />
                </div>

                {selectedRows.some((selected: boolean) => selected) &&
                    <div className="form-responses-selected-menu">

                        {selectedRows.filter((selected: boolean) => selected).length} row selected 
                        <Download size={16} onClick={handleDownloadSelected} />
                        {/* <img 
                            src={iconPath(ICONS.DOWNLOAD, storeUser)}
                            onClick={handleDownloadSelected}
                        /> */}

                        <Trash size={16} onClick={()=>setShowDeleteConfirm(true)} />

                        {/* <img 
                            src={iconPath(ICONS.DELETE, storeUser)}
                            onClick={()=>setShowDeleteConfirm(true)}
                        /> */}

                        <X size={16} onClick={handleClearSelections} />
                        {/* <img 
                            className="icon-close"
                            onClick={handleClearSelections}
                        /> */}
                    </div>
                }
            </div>

            {detailQuestionId &&
                <ResponseDetails 
                    form={form}
                    originalFormName={originalFormName || ""}
                    responses={formResponses}
                    questionId={detailQuestionId}
                    onClose={()=>setDetailQuestionId(null)}
                />
            }

            {showTableSettings && form &&
                <FormResponsesTableSettings
                    isOpen={showTableSettings}
                    form={form}
                    questions={getFormQuestionsInOrder(form)}
                    hiddenQuestionIds={hiddenQuestionIds}
                    onClose={()=>setShowTableSettings(false)}
                    onSubmit={(newHiddenQuestionIds: string[])=> {
                        setHiddenQuestionIds(newHiddenQuestionIds)
                        setShowTableSettings(false)
                    }}
                />
            }

            {showDeleteConfirm && 
                <FormResponsesDeleteModal
                    isOpen={showDeleteConfirm}
                    count={selectedRows.filter((selected: boolean) => selected).length}
                    onClose={()=>setShowDeleteConfirm(false)}
                    onSubmit={handleDeleteSelected}
                />
            }


            <Toaster />

        </>
        
    )
}